import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Pressable,
  ScrollView,
  Text,
  TextInput,
} from "react-native";
import type { EditorStyles } from "./styles";
import { stateAria } from "../a11y";

export type SelectOption = { value: string; label: string };

// A react-native <select> replacement (RNW has no native one): a tappable
// field that opens a modal option list. Optionally searchable for long
// lists (the exercise catalog). One primitive, reused for the intensity
// kind picker (short) and the exercise picker (long, searchable).
export function Select({
  value,
  options,
  onChange,
  placeholder,
  searchable,
  styles,
  accessibilityLabel,
}: {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  styles: EditorStyles;
  accessibilityLabel: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const current = options.find((o) => o.value === value);
  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      <Pressable
        style={styles.select}
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityValue={{ text: current?.label ?? placeholder ?? "" }}
      >
        <Text style={current ? styles.selectText : styles.selectPlaceholder}>
          {current?.label ?? placeholder ?? ""}
        </Text>
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={close}
      >
        <Pressable style={styles.modalScrim} onPress={close}>
          {/* Inner press is swallowed so tapping the sheet doesn't close. */}
          <Pressable style={styles.modalSheet} onPress={() => {}}>
            {searchable && (
              <TextInput
                style={styles.modalSearch}
                value={query}
                onChangeText={setQuery}
                placeholder={placeholder ?? t("program.exercisePick")}
                autoFocus
                accessibilityLabel={accessibilityLabel}
              />
            )}
            <ScrollView
              keyboardShouldPersistTaps="handled"
              accessibilityRole="radiogroup"
              accessibilityLabel={accessibilityLabel}
            >
              {filtered.map((o) => {
                const on = o.value === value;
                return (
                  <Pressable
                    key={o.value}
                    style={[styles.option, on && styles.optionOn]}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: on }}
                    {...stateAria({ selected: on }, "radio")}
                    onPress={() => {
                      onChange(o.value);
                      close();
                    }}
                  >
                    <Text style={[styles.optionText, on && styles.optionTextOn]}>
                      {o.label}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}
